import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, Subscription, take } from 'rxjs';
import {
    AvailableParentCustomOutcomeRequestModel,
    AvailableParentOutcomeRequestModel,
    AvailableParentProjectOutcomeModel,
    CustomOutcomeParentCustomOutcomeModel,
    OutcomeParentOutcomeModel,
    ProjectOutcomeModel
} from '../../../../../../../hub_schema/hubTypes';
import { OutcomesEditService } from '../../../services/outcomes-edit.service';

@Component({
    selector: 'app-link-strategy-outcomes-dialog',
    templateUrl: './link-strategy-outcomes-dialog.component.html',
    styleUrl: './link-strategy-outcomes-dialog.component.scss',
    standalone: false
})
export class LinkStrategyOutcomesDialogComponent implements OnInit, OnDestroy {
    private outcomesEditService: OutcomesEditService = inject(OutcomesEditService);
    private dialogRef: MatDialogRef<LinkStrategyOutcomesDialogComponent> = inject(MatDialogRef<LinkStrategyOutcomesDialogComponent>);
    private dialogData: { projectOutcome: ProjectOutcomeModel } = inject(MAT_DIALOG_DATA);

    public projectOutcome: ProjectOutcomeModel;

    public filterForm: FormGroup = new FormGroup({
        searchTerm: new FormControl('')
    });

    public availableParentOutcomes: AvailableParentProjectOutcomeModel[] | undefined;
    public pagedAvailableParentOutcomes: AvailableParentProjectOutcomeModel[] | undefined;

    private filterChangedSubscription: Subscription | undefined;

    private _currentPage: number = 0;

    public get currentPage(): number {
        return this._currentPage;
    }

    public set currentPage(value: number) {
        this._currentPage = value;
        this.pagedAvailableParentOutcomes = this.availableParentOutcomes?.slice(this._currentPage * 10, (this._currentPage * 10) + 10);
    }

    public get totalPages(): number {
        if (!this.availableParentOutcomes) {
            return 0;
        }
        return Math.ceil(this.availableParentOutcomes.length / 10);
    }

    public get isTncGoalsOutcome(): boolean {
        return !!this.projectOutcome.outcome;
    }

    public ngOnInit(): void {
        this.projectOutcome = this.dialogData.projectOutcome;
        this.loadAvailableParentOutcomes();

        this.filterChangedSubscription = this.filterForm.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => {
                this.currentPage = 0;
                this.loadAvailableParentOutcomes();
            });
    }

    private loadAvailableParentOutcomes(): void {
        if (this.isTncGoalsOutcome) {
            const request: AvailableParentOutcomeRequestModel = {
                projectId: this.projectOutcome.projectId,
                outcomeMetricId: this.projectOutcome.outcome!.outcomeMetricId,
                searchTerm: this.filterForm.controls.searchTerm?.value ?? ''
            };

            this.outcomesEditService.getAvailableParentOutcomes(request)
                .pipe(take(1))
                .subscribe((result: AvailableParentProjectOutcomeModel[]) => {
                    this.availableParentOutcomes = result;
                    this.currentPage = 0;
                });
        } else {
            const request: AvailableParentCustomOutcomeRequestModel = {
                projectId: this.projectOutcome.projectId,
                indicatorName: this.projectOutcome.customOutcome?.indicatorName!,
                searchTerm: this.filterForm.controls.searchTerm?.value ?? ''
            };

            this.outcomesEditService.getAvailableParentCustomOutcomes(request)
                .pipe(take(1))
                .subscribe((result: AvailableParentProjectOutcomeModel[]) => {
                    this.availableParentOutcomes = result;
                    this.currentPage = 0;
                });
        }
    }

    public pageRequested(page: number): void {
        this.currentPage = page;
    }

    public isAlreadyLinked(availableParent: AvailableParentProjectOutcomeModel): boolean {
        if (this.isTncGoalsOutcome) {
            const existingParents = this.projectOutcome.outcome!.parentOutcomes ?? [];
            return existingParents.some((p: OutcomeParentOutcomeModel) =>
                p.parentOutcomeId === availableParent.projectOutcome.outcome!.outcomeId
            );
        } else {
            const existingParents = this.projectOutcome.customOutcome!.parentCustomOutcomes ?? [];
            return existingParents.some((p: CustomOutcomeParentCustomOutcomeModel) =>
                p.parentCustomOutcomeId === availableParent.projectOutcome.customOutcome!.customOutcomeId
            );
        }
    }

    public linkParent(availableParent: AvailableParentProjectOutcomeModel): void {
        if (this.isTncGoalsOutcome) {
            const newParent: OutcomeParentOutcomeModel = {
                outcomeId: this.projectOutcome.outcome!.outcomeId!,
                parentOutcomeId: availableParent.projectOutcome.outcome!.outcomeId!,
                parentOutcomeProjectId: availableParent.strategyId,
                parentOutcomeProjectName: availableParent.strategyName,
                outcomeStatement: availableParent.projectOutcome.statement,
                linkageNotes: ''
            };

            if (!this.projectOutcome.outcome!.parentOutcomes) {
                this.projectOutcome.outcome!.parentOutcomes = [];
            }

            this.projectOutcome.outcome!.parentOutcomes.push(newParent);
        } else {
            const newParent: CustomOutcomeParentCustomOutcomeModel = {
                customOutcomeId: this.projectOutcome.customOutcome!.customOutcomeId!,
                parentCustomOutcomeId: availableParent.projectOutcome.customOutcome!.customOutcomeId!,
                parentCustomOutcomeProjectId: availableParent.strategyId,
                parentCustomOutcomeProjectName: availableParent.strategyName,
                outcomeStatement: availableParent.projectOutcome.statement,
                linkageNotes: ''
            };

            if (!this.projectOutcome.customOutcome!.parentCustomOutcomes) {
                this.projectOutcome.customOutcome!.parentCustomOutcomes = [];
            }

            this.projectOutcome.customOutcome!.parentCustomOutcomes.push(newParent);
        }
    }

    public close(): void {
        this.dialogRef.close();
    }

    public ngOnDestroy(): void {
        if (this.filterChangedSubscription) {
            this.filterChangedSubscription.unsubscribe();
        }
    }
}
